import { useCallback, useEffect, useMemo } from 'react';
import { atom, useAtom } from 'jotai';
import { useFixedUIHidden, useMatchesHandleData } from '~/modules/root';
import {
    CHAT_BOX_CLOSED_WIDTH_REM,
    CHAT_BOX_OPEN_WIDTH_PX,
    DRAWER_MARGIN_REM,
    DRAWER_WIDTH_REM,
    SPACING_PX,
} from '../utils';

type ChatInboxState = 'open' | 'closed' | 'hidden';

interface ChatBoxState {
    /**
     * The conversation ID. This is `0` for the new message chat box.
     */
    id: number;
    /**
     * The professional ID if this chat box was opened from the company inbox
     * and is being viewed from the perspective of the professional. If `null`,
     * provided, the personal inbox context will be used.
     */
    professionalID: number | null;
    /**
     * Whether the chat box is open or minimised.
     */
    isOpen: boolean;
}

const MAX_CHAT_BOXES = 5;

/**
 * The chat inbox drawer state.
 * - open: The chat inbox drawer is open.
 * - closed: The chat inbox drawer is closed but the button is visible.
 * - hidden: The chat inbox drawer button and chat boxes are hidden.
 */
const chatInboxStateAtom = atom<ChatInboxState>('closed');
/**
 * The list of chat boxes and their open/minimised states. This is ordered from
 * the oldest opened to the newest opened.
 */
const chatBoxStatesAtom = atom<ChatBoxState[]>([]);
/**
 * Hook to handle the floating chat widget UI. This includes the chat inbox
 * drawer state, and the chat box states. This hook can:
 * - Open and close the chat inbox drawer
 * - Open, close, and minimise chat boxes.
 * - Convert a new message chat box to an existing conversation chat box.
 */
export function useChatBoxes() {
    const isChatBoxesHidden = useFixedUIHidden();
    const isChatBoxesDisabled = useMatchesHandleData(
        'isChatBoxesDisabled',
        false,
    );
    const [chatInboxState, setChatInboxState] = useAtom(chatInboxStateAtom);
    const [chatBoxStates, setChatBoxStates] = useAtom(chatBoxStatesAtom);
    const isInboxOrChatBoxOpen = useMemo(() => {
        return chatInboxState === 'open' || chatBoxStates.some((c) => c.isOpen);
    }, [chatInboxState, chatBoxStates]);
    const isNewMessageChatBoxOpen = useMemo(() => {
        return chatBoxStates.some((c) => c.id === 0);
    }, [chatBoxStates]);

    const isChatBoxOpen = useCallback(
        (conversationID: number, professionalID: number | null): boolean => {
            return chatBoxStates.some(
                (c) =>
                    isChatBoxMatch(c, conversationID, professionalID) &&
                    c.isOpen,
            );
        },
        [chatBoxStates],
    );

    const openChatBox = useCallback(
        (conversationID: number, professionalID: number | null) => {
            setChatBoxStates((prev) => {
                const currentState = prev.find((c) =>
                    isChatBoxMatch(c, conversationID, professionalID),
                );
                // If the chat box is already open, do nothing.
                if (currentState?.isOpen) {
                    return prev;
                }
                let newState = minimiseOrCloseOtherChatBoxes(
                    prev,
                    conversationID,
                    professionalID,
                    !!currentState,
                );
                // If the chat box is minimised, open it.
                if (currentState) {
                    return newState.map((c) =>
                        isChatBoxMatch(c, conversationID, professionalID)
                            ? { ...c, isOpen: true }
                            : c,
                    );
                }
                // If the chat box is not open, open it.
                newState = [
                    ...newState,
                    {
                        id: conversationID,
                        professionalID,
                        isOpen: true,
                    },
                ];
                // Limit the maximum number of chat boxes.
                if (newState.length > MAX_CHAT_BOXES) {
                    newState.shift();
                }
                return newState;
            });
        },
        [setChatBoxStates],
    );

    const openMinimisedChatBox = useCallback(
        (conversationID: number, professionalID: number | null) => {
            setChatBoxStates((prev) => {
                const rootFontSize = parseFloat(
                    getComputedStyle(document.documentElement).fontSize,
                );
                if (
                    prev.some((c) =>
                        isChatBoxMatch(c, conversationID, professionalID),
                    ) ||
                    prev.length >= MAX_CHAT_BOXES ||
                    calculateAvailableSpace(prev) <
                        CHAT_BOX_CLOSED_WIDTH_REM * rootFontSize + SPACING_PX
                ) {
                    return prev;
                }
                return [
                    ...prev,
                    { id: conversationID, professionalID, isOpen: false },
                ];
            });
        },
        [setChatBoxStates],
    );

    const hideInboxAndChatBoxes = useCallback(() => {
        setChatInboxState((prev) => (prev === 'open' ? 'closed' : prev));
        setChatBoxStates((prev) => prev.map((c) => ({ ...c, isOpen: false })));
    }, [setChatInboxState, setChatBoxStates]);

    const convertNewMessageToConversation = useCallback(
        (conversationID: number, professionalID: number | null) => {
            if (conversationID <= 0) return;
            setChatBoxStates((prev) => {
                return prev
                    .filter(
                        (c) =>
                            !isChatBoxMatch(c, conversationID, professionalID),
                    )
                    .map((c) =>
                        c.id === 0
                            ? {
                                  id: conversationID,
                                  professionalID,
                                  isOpen: true,
                              }
                            : c,
                    );
            });
        },
        [setChatBoxStates],
    );

    // Close the chat boxes when navigating to a page with chat boxes disabled.
    useEffect(() => {
        if (isChatBoxesDisabled) {
            hideInboxAndChatBoxes();
        }
    }, [isChatBoxesDisabled, hideInboxAndChatBoxes]);

    return {
        /**
         * The chat inbox drawer state.
         */
        chatInboxState,
        /**
         * The list of chat boxes and their open/minimised states.
         */
        chatBoxStates,
        /**
         * Whether the chat boxes are disabled. This depends on the page/route handle data.
         */
        isChatBoxesDisabled,
        /**
         * Whether chat should be visually hidden.
         */
        isChatBoxesHidden,
        /**
         * Whether the chat inbox drawer or any chat box is open.
         */
        isInboxOrChatBoxOpen,
        /**
         * Whether the new message chat box (conversationID=0) is open or
         * minimised in the floating chat widget.
         */
        isNewMessageChatBoxOpen,
        /**
         * Set the chat inbox drawer state.
         */
        setChatInboxState,
        /**
         * Check if the chat box for a conversation is open.
         */
        isChatBoxOpen,
        /**
         * Open the chat box for a conversation. If there is no available screen
         * space, the oldest chat box(es) will be minimised or closed. Pass `0`
         * as the conversation ID to create a new conversation.
         */
        openChatBox,
        /**
         * Open a chat box in the minimised state it is not opened/minimised
         * already and there is space available. If the maximum number of chat
         * boxes is reached or there is no available space, do nothing.
         */
        openMinimisedChatBox,
        /**
         * Minimize the chat box for a conversation.
         */
        minimizeChatBox: useCallback(
            (conversationID: number, professionalID: number | null) => {
                setChatBoxStates((prev) => {
                    return prev.map((c) =>
                        isChatBoxMatch(c, conversationID, professionalID)
                            ? { ...c, isOpen: false }
                            : c,
                    );
                });
            },
            [setChatBoxStates],
        ),
        /**
         * Close the chat box for a conversation.
         */
        closeChatBox: useCallback(
            (conversationID: number, professionalID: number | null) => {
                setChatBoxStates((prev) => {
                    return prev.filter(
                        (c) =>
                            !isChatBoxMatch(c, conversationID, professionalID),
                    );
                });
            },
            [setChatBoxStates],
        ),
        /**
         * Close the inbox drawer and minimise all of the chat boxes.
         */
        hideInboxAndChatBoxes,
        /**
         * Convert the new message chat box to a conversation chat box. If the
         * new message chat box is not open, this will do nothing except close
         * the conversation chat box it exists.
         */
        convertNewMessageToConversation,
    };
}

/**
 * Check if a chat box matches a conversation and professional ID.
 */
function isChatBoxMatch(
    chatBox: ChatBoxState,
    conversationID: number,
    professionalID: number | null,
): boolean {
    return (
        chatBox.id === conversationID &&
        chatBox.professionalID === professionalID
    );
}

/**
 * Minimise or close other chat boxes to make space for the new chat box to open.
 * @param chatBoxes The list of chat boxes.
 * @param conversationID The ID of the conversation to be opened.
 * @param professionalID The professional ID if in a company inbox context.
 * @param isMinimised The chat box to open is currently minimised, instead of closed.
 * @returns The list of chat boxes after minimising or closing other chat boxes.
 */
function minimiseOrCloseOtherChatBoxes(
    chatBoxes: ChatBoxState[],
    conversationID: number,
    professionalID: number | null,
    isMinimised: boolean,
): ChatBoxState[] {
    const requiredSpace = getChatBoxRequiredSpace(isMinimised);
    let newState = [...chatBoxes];
    // First, minimise open chat boxes one by one until there's enough space.
    let availableSpace = calculateAvailableSpace(newState);
    while (availableSpace < requiredSpace) {
        const indexToMinimise = newState.findIndex(
            (c) =>
                c.isOpen && !isChatBoxMatch(c, conversationID, professionalID),
        );
        if (indexToMinimise === -1) {
            // No more open chat boxes to minimise.
            break;
        }
        // Minimise this chat box.
        newState[indexToMinimise] = {
            ...newState[indexToMinimise]!,
            isOpen: false,
        };
        availableSpace = calculateAvailableSpace(newState);
    }

    // If there's still not enough space, close chat boxes one by one.
    while (availableSpace < requiredSpace) {
        const indexToClose = newState.findIndex(
            (c) => !isChatBoxMatch(c, conversationID, professionalID),
        );
        if (indexToClose === -1) {
            // No more chat boxes to close.
            break;
        }
        // Close this chat box.
        newState.splice(indexToClose, 1);
        availableSpace = calculateAvailableSpace(newState);
    }

    return newState;
}

/**
 * Calculate the available space for a new chat box. This is calculated by
 * taking the window width and subtracting the width of the other chat boxes.
 * @param chatBoxes The list of chat boxes.
 * @returns The available space for new chat boxes in pixels. The value is
 *  clamped between 0 and 1920.
 */
function calculateAvailableSpace(chatBoxes: ChatBoxState[]): number {
    const windowWidth = window.visualViewport?.width || window.innerWidth;
    const rootFontSize = parseFloat(
        getComputedStyle(document.documentElement).fontSize,
    );
    const drawerWidthPx = (DRAWER_WIDTH_REM + DRAWER_MARGIN_REM) * rootFontSize;

    const chatBoxesWidthPx = chatBoxes.reduce((acc, chatBox) => {
        return (
            acc +
            (chatBox.isOpen
                ? CHAT_BOX_OPEN_WIDTH_PX
                : CHAT_BOX_CLOSED_WIDTH_REM * rootFontSize) +
            SPACING_PX
        );
    }, 0);

    return Math.min(
        Math.max(windowWidth - drawerWidthPx - chatBoxesWidthPx, 0),
        1920,
    );
}

/**
 * Get the required space for a chat box in pixels.
 * @param isMinimised Whether the chat box is minimised or closed.
 * @returns The required space for a chat box in pixels.
 */
function getChatBoxRequiredSpace(isMinimised: boolean): number {
    if (!isMinimised) {
        return CHAT_BOX_OPEN_WIDTH_PX + SPACING_PX;
    }
    const rootFontSize = parseFloat(
        getComputedStyle(document.documentElement).fontSize,
    );
    return CHAT_BOX_OPEN_WIDTH_PX - CHAT_BOX_CLOSED_WIDTH_REM * rootFontSize;
}
